import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const CLUSTER_NAMES = [
  "Semantic Data Set",
  "Open Source Data Points",
  "Intelligent Breach Detection",
  "Cluttered Raw Data",
];

/**
 * "Protocol // Convergence" — connected, interactive neural convergence system.
 *
 * Field view: several convergence clusters drift in controlled motion, each
 * linked internally and connected to every other cluster by delicate,
 * gently-pulsing gold/silver inter-cluster pathways — fragmented on the
 * surface, structurally one hidden neural system beneath.
 *
 * Hover (desktop): the cluster under the cursor brightens, its connecting
 * pathways pulse, nearby nodes activate, and a faint "ready to expand" ring
 * appears.
 *
 * Click / tap a cluster: a cinematic zoom (dolly + crossfade + focus blur)
 * enters that cluster, which fills the whole panel and reveals a detailed
 * internal structure — larger nodes, internal gold/silver micro-links,
 * subtle particle drift and convergence vectors firing node-to-node.
 *
 * Click inside the zoomed cluster: dark glass output panel —
 * "Developing and Processing Neural Convergence Points".
 *
 * RETURN TO FIELD (or Esc / click outside): smooth collapse, the connected
 * cluster system reforms. No navigation, no close icon. Canvas 2D, Triarch
 * palette only, paused when off-screen, reduced-motion aware.
 */
export const ProtocolConvergence = () => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const pointerRef = useRef({ x: -9999, y: -9999, nx: 0, ny: 0, inside: false });
  const clickRef = useRef(null);
  const zoomRef = useRef(-1);
  const kickRef = useRef(null);
  const hideTimer = useRef(null);

  const [output, setOutput] = useState({ visible: false, x: 0, y: 0, w: 320 });
  const [zoomed, setZoomed] = useState(null);

  const showOutput = (x, y) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    const W = rect ? rect.width : 600;
    const H = rect ? rect.height : 440;
    const w = Math.min(340, W - 24);
    const h = 104;
    const px = Math.max(12, Math.min(x - w / 2, W - w - 12));
    const py = Math.max(12, Math.min(y + 18, H - h - 12));
    setOutput({ visible: true, x: px, y: py, w });
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setOutput((o) => ({ ...o, visible: false })), 5200);
  };

  const enterZoom = (idx) => {
    zoomRef.current = idx;
    setZoomed(idx);
    setOutput((o) => ({ ...o, visible: false }));
    kickRef.current && kickRef.current();
  };

  const returnField = () => {
    zoomRef.current = -1;
    setZoomed(null);
    setOutput((o) => ({ ...o, visible: false }));
    kickRef.current && kickRef.current();
  };

  // bridge so the canvas loop can call the latest React actions
  const apiRef = useRef({});
  apiRef.current.showOutput = showOutput;
  apiRef.current.enterZoom = enterZoom;

  // click anywhere outside the panel -> collapse + close output
  useEffect(() => {
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        if (zoomRef.current >= 0) returnField();
        else setOutput((o) => (o.visible ? { ...o, visible: false } : o));
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isMobile = window.matchMedia("(max-width: 640px)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const GOLD = [203, 174, 120];
    const CHAMPAGNE = [212, 190, 150];
    const BRASS = [168, 140, 88];
    const SILVER = [150, 158, 170];
    const WHITE = [206, 212, 220];
    const MONO = "'IBM Plex Mono', ui-monospace, monospace";

    const N = isMobile ? 56 : 130;
    const K = isMobile ? 3 : 4;
    const M = isMobile ? 16 : 30; // inner detail nodes per cluster
    const PARALLAX = isMobile ? 6 : 15;
    const HOVER_R = isMobile ? 70 : 100;
    const MAX_PULSES = isMobile ? 22 : 48;
    const ZOOM = isMobile ? 3.0 : 3.4;

    const ANCHORS_D = [
      [0.29, 0.33],
      [0.63, 0.27],
      [0.45, 0.66],
      [0.76, 0.58],
    ];
    const ANCHORS_M = [
      [0.31, 0.32],
      [0.66, 0.4],
      [0.49, 0.68],
    ];

    let W = 0;
    let H = 0;
    let hubs = [];
    let pts = [];
    let links = [];
    let innerMaxR = 60;

    const rand = (a, b) => a + Math.random() * (b - a);
    const ease = (x) => 1 - Math.pow(1 - x, 3);
    const rgba = (c, a) => "rgba(" + c[0] + "," + c[1] + "," + c[2] + "," + Math.max(0, a).toFixed(3) + ")";
    const goldish = (c) => c === GOLD || c === CHAMPAGNE || c === BRASS;

    const buildInner = () => {
      innerMaxR = (Math.min(W, H) * 0.4) / ZOOM;
      const nodes = [];
      for (let m = 0; m < M; m++) {
        const ang = rand(0, Math.PI * 2);
        const radius = innerMaxR * Math.sqrt(Math.random());
        const r2 = Math.random();
        const col = r2 < 0.16 ? GOLD : r2 < 0.24 ? CHAMPAGNE : r2 < 0.58 ? WHITE : SILVER;
        nodes.push({
          ox: Math.cos(ang) * radius,
          oy: Math.sin(ang) * radius * 0.9,
          baseAng: ang,
          baseR: radius,
          size: rand(1.4, 3.0),
          ph: rand(0, Math.PI * 2),
          sp: rand(0.5, 1.2),
          col,
        });
      }
      const inLinks = [];
      const seen = new Set();
      for (let i = 0; i < M; i++) {
        const di = [];
        for (let j = 0; j < M; j++) {
          if (i === j) continue;
          const dx = nodes[i].ox - nodes[j].ox;
          const dy = nodes[i].oy - nodes[j].oy;
          di.push([dx * dx + dy * dy, j]);
        }
        di.sort((a, b) => a[0] - b[0]);
        const nn = isMobile ? 2 : 3;
        for (let k = 0; k < Math.min(nn, di.length); k++) {
          const j = di[k][1];
          const key = i < j ? i + "_" + j : j + "_" + i;
          if (seen.has(key)) continue;
          seen.add(key);
          inLinks.push([i, j]);
        }
        if (Math.random() < 0.5) inLinks.push([i, -1]); // -1 = centre
      }
      return { nodes, inLinks, pulses: [], spawn: 0 };
    };

    const build = () => {
      const anchors = isMobile ? ANCHORS_M : ANCHORS_D;
      hubs = [];
      for (let i = 0; i < K; i++) {
        const a = anchors[i % anchors.length];
        hubs.push({
          bx: W * a[0],
          by: H * a[1],
          cx: W * a[0],
          cy: H * a[1],
          c: [GOLD, BRASS, CHAMPAGNE, SILVER][i % 4],
          depth: rand(0.7, 1),
          driftPh: rand(0, Math.PI * 2),
          driftR: rand(6, 16),
          act: 0,
          pulse: 0,
          inner: buildInner(),
        });
      }

      pts = [];
      for (let i = 0; i < N; i++) {
        const hub = i % K;
        const r = Math.random();
        const col = r < 0.12 ? GOLD : r < 0.2 ? CHAMPAGNE : r < 0.26 ? BRASS : r < 0.6 ? WHITE : SILVER;
        pts.push({
          hub,
          hx: rand(W * 0.04, W * 0.96),
          hy: rand(H * 0.08, H * 0.92),
          ang: rand(0, Math.PI * 2),
          rad: rand(10, isMobile ? 58 : 84),
          depth: rand(0.28, 1),
          size: rand(0.8, 2.0),
          ph: rand(0, Math.PI * 2),
          sp: rand(0.5, 1.3),
          col,
          cx: 0,
          cy: 0,
          bright: 0,
        });
      }

      links = [];
      const seen = new Set();
      const neighbours = isMobile ? 1 : 2;
      for (let i = 0; i < N; i++) {
        const di = [];
        for (let j = 0; j < N; j++) {
          if (i === j || pts[i].hub !== pts[j].hub) continue;
          const dx = pts[i].hx - pts[j].hx;
          const dy = pts[i].hy - pts[j].hy;
          di.push([dx * dx + dy * dy, j]);
        }
        di.sort((a, b) => a[0] - b[0]);
        for (let nn = 0; nn < Math.min(neighbours, di.length); nn++) {
          const j = di[nn][1];
          const key = i < j ? i + "_" + j : j + "_" + i;
          if (seen.has(key)) continue;
          seen.add(key);
          links.push({ a: pts[i], b: pts[j], kind: "edge", birth: rand(1.4, 4.2), grow: rand(1.1, 2.2), base: rand(0.05, 0.13), c: SILVER });
        }
      }
      for (let i = 0; i < N; i++) {
        if (Math.random() < 0.5) continue;
        links.push({ a: pts[i], b: hubs[pts[i].hub], kind: "hub", birth: rand(2.0, 4.6), grow: rand(1.2, 2.0), base: rand(0.05, 0.12), c: goldish(pts[i].col) ? GOLD : SILVER });
      }
      // connect EVERY cluster to every other (one hidden field)
      for (let i = 0; i < K; i++) {
        for (let j = i + 1; j < K; j++) {
          links.push({
            a: hubs[i],
            b: hubs[j],
            hubA: i,
            hubB: j,
            kind: "interhub",
            birth: rand(2.4, 4.4),
            grow: rand(1.6, 2.6),
            base: 0.11,
            seed: rand(0, Math.PI * 2),
            c: [GOLD, SILVER, BRASS][(i + j) % 3],
          });
        }
      }
    };

    const resize = () => {
      const r = wrap.getBoundingClientRect();
      W = r.width;
      H = r.height;
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    };
    resize();

    let start = performance.now();
    let lastT = start;
    let running = true;
    let looping = false;
    let pnx = 0;
    let pny = 0;
    let zCur = 0;
    let selHub = -1;
    let lastBlur = 0;
    const pulses = [];
    let ambient = 0;

    const zTarget = () => (zoomRef.current >= 0 ? 1 : 0);
    const wantAnim = () => !reduced || zCur > 0.002 || zTarget() > 0.002;

    const spawnPulse = (count, near) => {
      for (let n = 0; n < count; n++) {
        if (pulses.length >= MAX_PULSES) break;
        let idx = -1;
        for (let tries = 0; tries < 6; tries++) {
          const k = Math.floor(Math.random() * links.length);
          const L = links[k];
          if (!L || L._prog < 0.85) continue;
          if (!near) {
            idx = k;
            break;
          }
          const mx = (L.a.cx + L.b.cx) / 2;
          const my = (L.a.cy + L.b.cy) / 2;
          const d = Math.hypot(mx - near.x, my - near.y);
          if (d < HOVER_R * 1.6 || tries === 5) {
            idx = k;
            break;
          }
        }
        if (idx < 0) continue;
        pulses.push({ k: idx, t: 0, dur: rand(0.7, 1.5), col: Math.random() < 0.74 ? GOLD : SILVER });
      }
    };

    // ---- field layer ---------------------------------------------------
    const drawField = (T, g, alpha) => {
      const ptr = pointerRef.current;
      ctx.globalAlpha = alpha;

      // hovered cluster (field mode only)
      let hoverHub = -1;
      if (ptr.inside && zCur < 0.4) {
        let bd = HOVER_R * 1.5;
        for (let i = 0; i < K; i++) {
          const d = Math.hypot(hubs[i].cx - ptr.x, hubs[i].cy - ptr.y);
          if (d < bd) {
            bd = d;
            hoverHub = i;
          }
        }
      }

      // particles
      const spin = T * 0.05;
      for (let i = 0; i < N; i++) {
        const p = pts[i];
        const hub = hubs[p.hub];
        const rEff = p.rad * (1 - 0.5 * g);
        const tx = hub.cx + Math.cos(p.ang + spin) * rEff;
        const ty = hub.cy + Math.sin(p.ang + spin) * rEff * 0.82;
        const dxp = pnx * p.depth * PARALLAX;
        const dyp = pny * p.depth * PARALLAX;
        const wob = (1 - g * 0.6) * (3 + p.depth * 4);
        let x = p.hx + (tx - p.hx) * g + Math.sin(T * p.sp + p.ph) * wob + dxp;
        let y = p.hy + (ty - p.hy) * g + Math.cos(T * p.sp * 0.9 + p.ph) * wob + dyp;
        let br = 0;
        if (ptr.inside && zCur < 0.4) {
          const d = Math.hypot(x - ptr.x, y - ptr.y);
          if (d < HOVER_R) {
            const f = 1 - d / HOVER_R;
            x += (ptr.x - x) * 0.1 * f;
            y += (ptr.y - y) * 0.1 * f;
            br = f;
          }
          if (p.hub === hoverHub) br = Math.max(br, 0.5);
        }
        p.cx = x;
        p.cy = y;
        p.bright = br;
      }

      // links
      for (let k = 0; k < links.length; k++) {
        const L = links[k];
        const prog = reduced ? 1 : Math.min(1, Math.max(0, (T - L.birth) / L.grow));
        L._prog = prog;
        if (prog <= 0.001) continue;
        const ax = L.a.cx;
        const ay = L.a.cy;
        const ex = ax + (L.b.cx - ax) * prog;
        const ey = ay + (L.b.cy - ay) * prog;
        const minDepth = Math.min(L.a.depth ?? 1, L.b.depth ?? 1);
        let a = L.base * (0.55 + minDepth * 0.6);
        let col = L.c;
        if (L.kind === "interhub") {
          a *= 0.72 + 0.28 * Math.sin(T * 0.7 + L.seed); // delicate low-level pulse
          if (hoverHub === L.hubA || hoverHub === L.hubB) a += 0.18;
        }
        if (ptr.inside && zCur < 0.4) {
          const mx = (ax + L.b.cx) / 2;
          const my = (ay + L.b.cy) / 2;
          const d = Math.hypot(mx - ptr.x, my - ptr.y);
          if (d < HOVER_R * 1.25) {
            const f = 1 - d / (HOVER_R * 1.25);
            a += f * 0.3;
            if (f > 0.25) col = Math.random() < 0.5 ? GOLD : CHAMPAGNE;
          }
        }
        ctx.beginPath();
        ctx.strokeStyle = rgba(col, Math.min(0.6, a));
        ctx.lineWidth = L.kind === "interhub" ? 0.9 : 1;
        ctx.moveTo(ax, ay);
        ctx.lineTo(ex, ey);
        ctx.stroke();
      }

      // particles draw
      for (let i = 0; i < N; i++) {
        const p = pts[i];
        let size = p.size * (0.6 + p.depth * 0.9);
        let a = 0.1 + p.depth * 0.34 + (goldish(p.col) ? 0.06 : 0);
        if (p.bright > 0) {
          a = Math.min(1, a + p.bright * 0.5);
          size += p.bright * 1.1;
        }
        if (p.bright > 0.35) {
          ctx.save();
          ctx.shadowBlur = 9;
          ctx.shadowColor = rgba(GOLD, 0.8);
          ctx.beginPath();
          ctx.fillStyle = rgba(p.col, a);
          ctx.arc(p.cx, p.cy, Math.max(0.4, size), 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        } else {
          ctx.beginPath();
          ctx.fillStyle = rgba(p.col, a);
          ctx.arc(p.cx, p.cy, Math.max(0.4, size), 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // field pulses
      if (!reduced) {
        for (let k = pulses.length - 1; k >= 0; k--) {
          const pu = pulses[k];
          const L = links[pu.k];
          if (!L || pu.t / pu.dur >= 1) {
            if (L && L.b.act !== undefined) L.b.act = Math.min(1, (L.b.act || 0) + 0.35);
            pulses.splice(k, 1);
            continue;
          }
          const f = pu.t / pu.dur;
          const x = L.a.cx + (L.b.cx - L.a.cx) * f;
          const y = L.a.cy + (L.b.cy - L.a.cy) * f;
          ctx.save();
          ctx.shadowBlur = 8;
          ctx.shadowColor = rgba(pu.col, 0.9);
          ctx.beginPath();
          ctx.fillStyle = rgba(pu.col, 1 - f * 0.6);
          ctx.arc(x, y, 1.7, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }

      // hubs
      for (let i = 0; i < K; i++) {
        const h = hubs[i];
        const appear = reduced ? 1 : Math.min(1, Math.max(0, (T - 1.2) / 2.2));
        if (appear <= 0) continue;
        const hov = i === hoverHub ? 1 : 0;
        const r = (isMobile ? 3.6 : 5) * appear + h.pulse * 3 + h.act * 2.4 + hov * 2;
        ctx.save();
        ctx.shadowBlur = 14 + h.pulse * 14 + h.act * 12 + hov * 14;
        ctx.shadowColor = rgba(h.c, 0.7);
        ctx.beginPath();
        ctx.fillStyle = rgba(h.c, (0.7 + h.pulse * 0.3) * appear);
        ctx.arc(h.cx, h.cy, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        ctx.beginPath();
        ctx.strokeStyle = rgba(h.c, (0.18 + h.act * 0.3 + hov * 0.25) * appear);
        ctx.lineWidth = 1;
        ctx.arc(h.cx, h.cy, r + 5 + h.act * 6, 0, Math.PI * 2);
        ctx.stroke();
        // "ready to expand" ring on hover
        if (hov) {
          const rr = r + 10 + ((T * 28) % 26);
          ctx.beginPath();
          ctx.strokeStyle = rgba(GOLD, 0.18 * Math.max(0, 1 - ((T * 28) % 26) / 26));
          ctx.lineWidth = 1;
          ctx.arc(h.cx, h.cy, rr, 0, Math.PI * 2);
          ctx.stroke();
        }
      }

      // embedded cluster identity labels (field mode)
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = (isMobile ? "8px " : "9px ") + MONO;
      if ("letterSpacing" in ctx) ctx.letterSpacing = "1.4px";
      for (let i = 0; i < K; i++) {
        const h = hubs[i];
        const appear = reduced ? 1 : Math.min(1, Math.max(0, (T - 1.6) / 2.4));
        if (appear <= 0) continue;
        const name = (CLUSTER_NAMES[i] || "").toUpperCase();
        const tw = ctx.measureText(name).width;
        const half = tw / 2 + 8;
        const lx = Math.max(half, Math.min(h.cx, W - half));
        const below = h.cy < H * 0.6;
        const ly = below ? h.cy + (isMobile ? 22 : 28) : h.cy - (isMobile ? 20 : 26);
        const hov = i === hoverHub;
        ctx.fillStyle = rgba(hov ? GOLD : SILVER, (hov ? 0.85 : 0.32) * appear);
        ctx.fillText(name, lx, ly);
      }
      if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";
      ctx.restore();

      ctx.globalAlpha = 1;
    };

    // ---- inner (zoomed) layer -----------------------------------------
    const drawInner = (hub, T, z, iScale) => {
      const ptr = pointerRef.current;
      const inner = hub.inner;
      const cxP = W / 2;
      const cyP = H / 2;
      ctx.globalAlpha = z;

      const pos = (node) => {
        const breathe = 1 - 0.06 * Math.sin(T * 0.6 + node.ph) * (1); // gentle compression
        const ox = node.ox * breathe + Math.sin(T * node.sp + node.ph) * 3;
        const oy = node.oy * breathe + Math.cos(T * node.sp * 0.9 + node.ph) * 3;
        return {
          x: cxP + ox * ZOOM * iScale + pnx * 8,
          y: cyP + oy * ZOOM * iScale + pny * 8,
        };
      };

      // precompute node screen positions
      const P = inner.nodes.map(pos);
      const coreP = { x: cxP + pnx * 8, y: cyP + pny * 8, r: (isMobile ? 5 : 7) + hub.pulse * 4 };
      inner._P = P;
      inner._core = coreP;
      inner._R = inner._R || [];

      // inner links
      for (let l = 0; l < inner.inLinks.length; l++) {
        const [i, j] = inner.inLinks[l];
        const a = P[i];
        const b = j === -1 ? { x: cxP + pnx * 8, y: cyP + pny * 8 } : P[j];
        ctx.beginPath();
        ctx.strokeStyle = rgba(goldish(inner.nodes[i].col) ? GOLD : SILVER, 0.24);
        ctx.lineWidth = 1;
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }

      // central convergence core
      ctx.save();
      ctx.shadowBlur = 26;
      ctx.shadowColor = rgba(hub.c, 0.7);
      ctx.beginPath();
      ctx.fillStyle = rgba(hub.c, 0.9);
      ctx.arc(cxP + pnx * 8, cyP + pny * 8, (isMobile ? 5 : 7) + hub.pulse * 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // inner nodes
      for (let i = 0; i < inner.nodes.length; i++) {
        const node = inner.nodes[i];
        const p = P[i];
        let size = node.size * (1.0 + iScale * 0.7);
        inner._R[i] = size;
        let a = 0.66 + 0.26 * Math.sin(T * node.sp + node.ph);
        let br = 0;
        if (ptr.inside) {
          const d = Math.hypot(p.x - ptr.x, p.y - ptr.y);
          if (d < HOVER_R * 0.8) br = 1 - d / (HOVER_R * 0.8);
        }
        const golden = goldish(node.col);
        // soft halo
        ctx.save();
        ctx.shadowBlur = golden ? 8 : 5;
        ctx.shadowColor = rgba(golden ? GOLD : SILVER, 0.7);
        ctx.beginPath();
        ctx.fillStyle = rgba(node.col, Math.min(1, a + br * 0.4));
        ctx.arc(p.x, p.y, size + br * 1.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        // crisp core
        ctx.beginPath();
        ctx.fillStyle = rgba(WHITE, Math.min(1, 0.5 + br * 0.5));
        ctx.arc(p.x, p.y, Math.max(0.6, size * 0.42), 0, Math.PI * 2);
        ctx.fill();
      }

      // inner pulses firing node-to-node
      if (!reduced && z > 0.55) {
        inner.spawn -= 0.016;
        if (inner.spawn <= 0 && inner.inLinks.length) {
          const li = Math.floor(Math.random() * inner.inLinks.length);
          inner.pulses.push({ li, t: 0, dur: rand(0.5, 1.0), col: Math.random() < 0.7 ? GOLD : SILVER });
          inner.spawn = ptr.inside ? rand(0.04, 0.12) : rand(0.12, 0.3);
          if (inner.pulses.length > (isMobile ? 16 : 34)) inner.pulses.shift();
        }
        for (let k = inner.pulses.length - 1; k >= 0; k--) {
          const pu = inner.pulses[k];
          pu.t += 0.016;
          const f = pu.t / pu.dur;
          if (f >= 1) {
            inner.pulses.splice(k, 1);
            continue;
          }
          const [i, j] = inner.inLinks[pu.li];
          const a = P[i];
          const b = j === -1 ? { x: cxP + pnx * 8, y: cyP + pny * 8 } : P[j];
          const x = a.x + (b.x - a.x) * f;
          const y = a.y + (b.y - a.y) * f;
          ctx.save();
          ctx.shadowBlur = 9;
          ctx.shadowColor = rgba(pu.col, 0.9);
          ctx.beginPath();
          ctx.fillStyle = rgba(pu.col, 1 - f * 0.5);
          ctx.arc(x, y, 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }
      ctx.globalAlpha = 1;
    };

    // distance from point p to segment a-b (screen space)
    const distToSeg = (p, a, b) => {
      const vx = b.x - a.x;
      const vy = b.y - a.y;
      const wx = p.x - a.x;
      const wy = p.y - a.y;
      const c1 = vx * wx + vy * wy;
      if (c1 <= 0) return Math.hypot(p.x - a.x, p.y - a.y);
      const c2 = vx * vx + vy * vy;
      if (c2 <= c1) return Math.hypot(p.x - b.x, p.y - b.y);
      const t = c1 / c2;
      return Math.hypot(p.x - (a.x + t * vx), p.y - (a.y + t * vy));
    };

    // true only if the click lands on an ACTIVE element of the zoomed cluster
    // (core, a node, or a vector pathway) — not empty space.
    const hitInner = (hub, cpos) => {
      const inner = hub.inner;
      if (!inner._P) return false;
      const core = inner._core;
      if (core && Math.hypot(cpos.x - core.x, cpos.y - core.y) < core.r + 14) return true;
      for (let i = 0; i < inner._P.length; i++) {
        const p = inner._P[i];
        const r = (inner._R[i] || 3) + 13;
        if (Math.hypot(cpos.x - p.x, cpos.y - p.y) < r) return true;
      }
      for (let l = 0; l < inner.inLinks.length; l++) {
        const [i, j] = inner.inLinks[l];
        const a = inner._P[i];
        const b = j === -1 ? core : inner._P[j];
        if (a && b && distToSeg(cpos, a, b) < 8) return true;
      }
      return false;
    };

    const frame = (now) => {
      const dt = Math.min((now - lastT) / 1000, 0.05);
      lastT = now;
      const T = (now - start) / 1000;
      const g = reduced ? 1 : ease(Math.min(1, T / 6.5));

      const zt = zTarget();
      zCur += (zt - zCur) * (reduced ? 0.16 : 0.09);
      if (zCur < 0.0015 && zt === 0) zCur = 0;

      const ptr = pointerRef.current;
      const targetNx = ptr.inside ? ptr.nx : 0;
      const targetNy = ptr.inside ? ptr.ny : 0;
      pnx += (targetNx - pnx) * 0.06;
      pny += (targetNy - pny) * 0.06;

      // hub update
      for (let i = 0; i < K; i++) {
        const h = hubs[i];
        h.act *= 0.93;
        h.pulse *= 0.92;
        const dxp = pnx * h.depth * PARALLAX;
        const dyp = pny * h.depth * PARALLAX;
        h.cx = h.bx + Math.cos(T * 0.12 + h.driftPh) * h.driftR + dxp;
        h.cy = h.by + Math.sin(T * 0.1 + h.driftPh) * h.driftR * 0.8 + dyp;
        if (ptr.inside && zCur < 0.4) {
          const d = Math.hypot(h.cx - ptr.x, h.cy - ptr.y);
          if (d < HOVER_R * 1.3) h.act = Math.min(1, h.act + (1 - d / (HOVER_R * 1.3)) * 0.12);
        }
      }

      // selected hub bookkeeping
      if (zoomRef.current >= 0) selHub = zoomRef.current;
      if (zCur < 0.01 && zt === 0) selHub = -1;

      // click processing — only ACTIVE elements respond
      if (clickRef.current) {
        const cpos = clickRef.current;
        clickRef.current = null;
        const api = apiRef.current;
        if (selHub >= 0 && zCur > 0.55) {
          // zoomed: output only when a node / vector / core is actually hit
          if (hitInner(hubs[selHub], cpos)) {
            hubs[selHub].pulse = 1;
            if (!reduced) spawnPulse(isMobile ? 6 : 12, cpos);
            api.showOutput && api.showOutput(cpos.x, cpos.y);
          }
        } else if (selHub < 0 && zCur < 0.4) {
          // field: enter a cluster only when clicking on/near it (not empty space)
          let bi = -1;
          let bd = Infinity;
          for (let i = 0; i < K; i++) {
            const d = Math.hypot(hubs[i].cx - cpos.x, hubs[i].cy - cpos.y);
            if (d < bd) {
              bd = d;
              bi = i;
            }
          }
          let np = Infinity;
          let nph = -1;
          for (let i = 0; i < N; i++) {
            const d = Math.hypot(pts[i].cx - cpos.x, pts[i].cy - cpos.y);
            if (d < np) {
              np = d;
              nph = pts[i].hub;
            }
          }
          const hubHitR = isMobile ? 66 : 92;
          let target = -1;
          if (bd <= hubHitR) target = bi;
          else if (np <= 16) target = nph;
          if (target >= 0) {
            hubs[target].pulse = 1;
            api.enterZoom && api.enterZoom(target);
          }
        }
      }

      // ambient field pulses
      if (!reduced && zCur < 0.6) {
        ambient -= dt;
        const rate = ptr.inside ? 0.06 : 0.22;
        if (ambient <= 0 && g > 0.4) {
          spawnPulse(ptr.inside ? 2 : 1, ptr.inside ? { x: ptr.x, y: ptr.y } : null);
          ambient = rate;
        }
      }

      ctx.clearRect(0, 0, W, H);

      // focus point for the dolly-in
      const focusX = selHub >= 0 ? hubs[selHub].cx : W / 2;
      const focusY = selHub >= 0 ? hubs[selHub].cy : H / 2;

      const fieldAlpha = 1 - zCur;
      if (fieldAlpha > 0.012) {
        ctx.save();
        if (zCur > 0.001) {
          const fs = 1 + zCur * 0.9;
          ctx.translate(focusX, focusY);
          ctx.scale(fs, fs);
          ctx.translate(-focusX, -focusY);
        }
        drawField(T, g, fieldAlpha);
        ctx.restore();
      }

      if (selHub >= 0 && zCur > 0.01) {
        const iScale = 0.6 + zCur * 0.4;
        drawInner(hubs[selHub], T, zCur, iScale);
      }

      // focus-pull blur during transition
      const blurPx = Math.min(5, Math.abs(zt - zCur) * 16);
      if (Math.abs(blurPx - lastBlur) > 0.15) {
        canvas.style.filter = blurPx > 0.25 ? "blur(" + blurPx.toFixed(1) + "px)" : "none";
        lastBlur = blurPx;
      }

      if (running && wantAnim()) {
        looping = true;
        rafRef.current = requestAnimationFrame(frame);
      } else {
        looping = false;
      }
    };

    kickRef.current = () => {
      if (running && !looping) {
        looping = true;
        lastT = performance.now();
        rafRef.current = requestAnimationFrame(frame);
      }
    };

    looping = true;
    rafRef.current = requestAnimationFrame(frame);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            running = true;
            kickRef.current();
          } else {
            running = false;
            cancelAnimationFrame(rafRef.current);
            looping = false;
          }
        });
      },
      { threshold: 0.04 }
    );
    io.observe(wrap);

    const onResize = () => {
      resize();
      start = performance.now();
      kickRef.current();
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      io.disconnect();
      window.removeEventListener("resize", onResize);
      kickRef.current = null;
    };
  }, []);

  // ---- pointer handlers -----------------------------------------------
  const onMove = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    pointerRef.current = {
      x,
      y,
      nx: (x / rect.width - 0.5) * 2,
      ny: (y / rect.height - 0.5) * 2,
      inside: true,
    };
  };
  const onLeave = () => {
    pointerRef.current = { ...pointerRef.current, x: -9999, y: -9999, inside: false };
  };
  const onClick = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    // record the click; the canvas loop decides whether an ACTIVE element was
    // hit (enter a cluster in field mode, or emit output in zoomed mode).
    clickRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    if (kickRef.current) kickRef.current();
  };
  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      if (zoomRef.current >= 0) returnField();
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const rect = wrapRef.current.getBoundingClientRect();
      if (zoomRef.current >= 0) {
        clickRef.current = { x: rect.width / 2, y: rect.height / 2 };
        showOutput(rect.width / 2, rect.height / 2);
      } else {
        enterZoom(0);
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="protocol-convergence"
      className="relative mx-auto overflow-hidden rounded-[1.4rem] border border-white/10 bg-white/[0.025] backdrop-blur-xl shadow-[0_24px_80px_rgba(0,0,0,0.5)]"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/[0.06] px-6 py-4">
        <div className="flex items-center gap-2.5">
          <span className="h-1.5 w-1.5 rounded-full bg-[#C4A061]/80 shadow-[0_0_10px_rgba(196,160,97,0.5)]" />
          <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-white/70">
            Protocol // Convergence
          </span>
        </div>
        <span
          data-testid="protocol-mode-label"
          className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-white/35"
        >
          {zoomed !== null ? "Convergence Layer" : "Neural Field"}
        </span>
      </div>

      <div
        ref={wrapRef}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        onClick={onClick}
        onKeyDown={onKeyDown}
        role="button"
        tabIndex={0}
        aria-label="Interactive neural convergence field. Activate a cluster to enter its convergence layer."
        data-testid="protocol-convergence-stage"
        className="relative h-[340px] w-full cursor-pointer select-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#C9AE78]/40 sm:h-[460px]"
      >
        <div className="pointer-events-none absolute inset-0 triarch-grid opacity-[0.14]" />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(620px 360px at 52% 48%, rgba(203,174,120,0.06), transparent 64%)",
          }}
        />
        <canvas
          ref={canvasRef}
          data-testid="protocol-convergence-canvas"
          aria-hidden="true"
          className="absolute inset-0 h-full w-full"
        />

        {/* field-mode labels */}
        <AnimatePresence>
          {zoomed === null && (
            <motion.div
              key="field-labels"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <span className="pointer-events-none absolute left-6 top-5 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-white/30">
                Fragmented
              </span>
              <span className="pointer-events-none absolute bottom-5 right-6 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-white/30">
                Structure
              </span>
              <span className="pointer-events-none absolute bottom-5 left-6 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-white/30">
                Hover to preview · Click to enter
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* zoomed-mode: convergence id + RETURN action */}
        <AnimatePresence>
          {zoomed !== null && (
            <motion.div
              key="zoom-ui"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, delay: 0.15 }}
            >
              <span className="pointer-events-none absolute left-6 top-5 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#C9AE78]/75">
                {String((zoomed ?? 0) + 1).padStart(2, "0")} —{" "}
                {CLUSTER_NAMES[zoomed] || "Convergence Cluster"}
              </span>
              <button
                type="button"
                data-testid="protocol-return-field"
                onClick={(e) => {
                  e.stopPropagation();
                  returnField();
                }}
                className="group absolute bottom-5 left-6 inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.24em] text-white/45 transition-colors duration-300 hover:text-white focus-visible:outline-none"
              >
                <span className="h-px w-5 bg-[#C9AE78]/50 transition-all duration-300 group-hover:w-8" />
                Return to Field
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {output.visible && (
            <motion.div
              data-testid="protocol-output"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 10, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: 8, filter: "blur(6px)" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              style={{ left: output.x, top: output.y, width: output.w }}
              className="absolute z-10 rounded-xl border border-[#C9AE78]/35 bg-[#0B0D10]/85 p-4 backdrop-blur-xl shadow-[0_0_34px_rgba(203,174,120,0.2)]"
            >
              <span className="font-mono-accent text-[10px] uppercase tracking-[0.28em] text-[#C9AE78]/85">
                Protocol Output // Convergence
              </span>
              <div className="mt-2 h-px w-full bg-gradient-to-r from-[#C9AE78]/30 to-transparent" />
              <p className="mt-3 font-mono-accent text-xs leading-relaxed text-white/80">
                Developing and Processing Neural Convergence Points
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
